"use client";

import * as React from "react";
import { createPortal } from "react-dom";

/**
 * Update Multiple for Equipment Time Card.
 * Applies one Time on Site (Hrs) value to the selected equipment rows in the parent grid.
 * Hours persist when the user clicks Save and Log Production.
 */

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
        fill="#424143"
      />
    </svg>
  );
}

const PARTIAL_HOURS_INPUT_RE = /^\d*\.?\d*$/;
const TABLE_COLUMNS =
  "grid w-full min-w-0 grid-cols-[88px_minmax(0,1fr)_110px] sm:min-w-[620px] sm:grid-cols-[1fr_1.2fr_1.4fr]";
const ROW_HEIGHT = "min-h-[56px] sm:h-[65px]";

const isValidPartialHoursInput = (value) =>
  value === "" || PARTIAL_HOURS_INPUT_RE.test(value);

const normalizeHoursOnBlur = (value) => {
  if (value === "" || value == null) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const toNumber = (value) => {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const equipmentLabel = (row) => {
  const unit = String(row?.unitNumber || "").trim();
  const description = String(row?.description || "").trim();
  if (unit && description) return `${unit} — ${description}`;
  return unit || description || "Equipment";
};

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   rows: Array<{ id: string, unitNumber?: string, description?: string, runTimeHours?: number|string }>,
 *   onUpdateRunTime: (payload: { rowIds: string[], runTimeHours: number }) => void,
 * }} props
 */
function EquipmentTimeCardUpdateMultipleModal({
  isOpen,
  onClose,
  rows = [],
  onUpdateRunTime,
}) {
  const [selectedIds, setSelectedIds] = React.useState({});
  const [runTimeInput, setRunTimeInput] = React.useState("");
  const [saveMessage, setSaveMessage] = React.useState(null);
  const selectAllRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setSelectedIds({});
    setRunTimeInput("");
    setSaveMessage(null);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const selectedRows = React.useMemo(
    () => (rows || []).filter((row) => !!selectedIds[String(row.id)]),
    [rows, selectedIds]
  );
  const selectedCount = selectedRows.length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  React.useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const selectedRunTimeTotal = React.useMemo(
    () =>
      round2(
        selectedRows.reduce((sum, row) => sum + toNumber(row.runTimeHours), 0)
      ).toFixed(2),
    [selectedRows]
  );

  const handleSelectionChange = (id, checked) => {
    setSelectedIds((prev) => ({ ...prev, [String(id)]: checked }));
    setSaveMessage(null);
  };

  const handleToggleAll = (checked) => {
    if (!checked) {
      setSelectedIds({});
    } else {
      const next = {};
      (rows || []).forEach((row) => {
        next[String(row.id)] = true;
      });
      setSelectedIds(next);
    }
    setSaveMessage(null);
  };

  const handleHoursChange = (value) => {
    if (!isValidPartialHoursInput(value)) return;
    setRunTimeInput(value);
    setSaveMessage(null);
  };

  const handleHoursBlur = () => {
    setRunTimeInput((current) => {
      if (current === "" || current == null) return "";
      const normalized = normalizeHoursOnBlur(current);
      return current === normalized ? current : normalized;
    });
  };

  const handleUpdateHours = () => {
    if (selectedCount === 0) {
      setSaveMessage({ type: "error", text: "Select at least one equipment row." });
      return;
    }

    const hoursText = String(runTimeInput ?? "").trim();
    onUpdateRunTime?.({
      rowIds: selectedRows.map((row) => String(row.id)),
      runTimeHours: hoursText === "" || hoursText === "." ? "" : toNumber(runTimeInput),
    });
    onClose?.();
  };

  if (!isOpen) return null;

  const canUpdate = selectedCount > 0;

  return createPortal(
    <div className="fixed inset-0 z-[10420] bg-black/50">
      <div className="absolute inset-2 lg:inset-6 flex min-h-0 justify-center">
        <div className="flex h-full max-h-full min-h-0 w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
          <header className="relative z-10 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-[18px] rounded-t-xl shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
            <h2 className="font-poppins text-[20px] font-bold text-[#2C2C2C]">
              Update Multiple
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100"
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4 sm:p-6 [-webkit-overflow-scrolling:touch]">
            {rows.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No equipment on this time card yet. Add equipment first.
              </div>
            ) : (
              <>
                <section>
                  <h2 className="text-base font-semibold text-neutral-700 mb-4">
                    Select Equipment
                  </h2>
                  <label className="mb-4 flex w-fit gap-2 items-center cursor-pointer">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleToggleAll(e.target.checked)}
                      aria-label="Select all equipment"
                      className="rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] shrink-0 cursor-pointer"
                    />
                    <span className="font-poppins text-[14px] font-semibold text-[#2C2C2C]">
                      Select all
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-4 content-center items-center max-sm:gap-3">
                    {rows.map((row) => {
                      const id = String(row.id);
                      const name = equipmentLabel(row);
                      return (
                        <label
                          key={id}
                          className="flex gap-2 items-center bg-white rounded-sm cursor-pointer max-w-full"
                        >
                          <input
                            type="checkbox"
                            checked={!!selectedIds[id]}
                            onChange={(e) => handleSelectionChange(id, e.target.checked)}
                            className="rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] shrink-0 cursor-pointer"
                          />
                          <span
                            className="font-poppins text-[14px] font-normal text-[#2C2C2C]"
                            title={name}
                          >
                            {name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>

                <section className="mt-6 w-full">
                  <div className="overflow-x-auto">
                    <div className="border border-gray-300 rounded-md overflow-hidden w-full min-w-0 sm:min-w-[620px]">
                      <header
                        className={`${TABLE_COLUMNS} ${ROW_HEIGHT} border-b border-gray-300`}
                      >
                        <div className="flex items-center justify-center bg-gray-200 h-full">
                          <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                            Total Run Time
                          </h3>
                        </div>
                        <div className="flex items-center justify-center bg-gray-200 h-full">
                          <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                            Field
                          </h3>
                        </div>
                        <div className="flex items-center justify-center bg-gray-200 h-full">
                          <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                            Time on Site (Hrs)
                          </h3>
                        </div>
                      </header>
                      <div className={`${TABLE_COLUMNS} ${ROW_HEIGHT}`}>
                        <div className="flex items-center justify-center bg-gray-200">
                          <span className="text-sm font-medium text-zinc-800">
                            {selectedRunTimeTotal}
                          </span>
                        </div>
                        <div className="flex items-center justify-center bg-[#EAF4FB] px-2">
                          <span className="text-sm text-center text-zinc-800">
                            Time on Site (Hrs)
                          </span>
                        </div>
                        <div className="flex items-center justify-center bg-white px-2 sm:px-6">
                          <div className="w-full max-w-[260px] flex items-center justify-center">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={runTimeInput}
                              onChange={(e) => handleHoursChange(e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onBlur={handleHoursBlur}
                              aria-label="Time on Site (Hrs) to apply"
                              placeholder="0"
                              className="w-full h-[38px] border border-gray-300 rounded text-sm font-normal text-[#2C2C2C] text-center outline-none bg-white placeholder:text-gray-400 placeholder:font-normal"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="mt-8 mb-2">
                  {saveMessage && (
                    <p
                      className={`text-sm text-center mb-2 ${
                        saveMessage.type === "success"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {saveMessage.text}
                    </p>
                  )}
                  <div className="max-w-[296px] mx-auto">
                    <button
                      type="button"
                      onClick={handleUpdateHours}
                      disabled={!canUpdate}
                      className={`inline-flex justify-center items-center h-[47px] w-full rounded text-white text-lg ${
                        canUpdate
                          ? "bg-[#AD2226] hover:bg-[#8F1B1E] cursor-pointer"
                          : "bg-gray-200 cursor-not-allowed"
                      }`}
                    >
                      Update Hours
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default EquipmentTimeCardUpdateMultipleModal;
